import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { LogOut, Plus, Edit, Trash2, Building2, Home as HomeIcon, X, Upload, Download, Link2, Send, Image as ImageIcon, TrendingUp, BookOpen, Shield, Facebook, FileText, Users, MessageSquare, Share2, Check } from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [quickEditImagesOpen, setQuickEditImagesOpen] = useState(false);
  const [quickEditProperty, setQuickEditProperty] = useState(null);
  const [quickImageUrls, setQuickImageUrls] = useState('');
  const [quickImageAction, setQuickImageAction] = useState('add');
  const [lineVoomExtractUrl, setLineVoomExtractUrl] = useState('');
  const [extractingImages, setExtractingImages] = useState(false);
  const [formData, setFormData] = useState({
    title: '', description: '', property_type: 'House', listing_type: 'Sale',
    rental_period: '', price: '', location: '', city: '', bedrooms: '', bathrooms: '',
    size_sqm: '', amenities: [], images: [], videos: [], status: 'active', co_agent: false, banner: null, featured: false
  });
  const [newAmenity, setNewAmenity] = useState('');
  const [imageInput, setImageInput] = useState('');
  const [videoInput, setVideoInput] = useState('');
  const [fbConfigured, setFbConfigured] = useState(false);
  const [lineConfigured, setLineConfigured] = useState(false);

  useEffect(() => { fetchProperties(); checkSocialStatus(); }, []);

  const checkSocialStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/social/status`, { headers: { Authorization: `Bearer ${token}` } });
      setFbConfigured(res.data.facebook_configured);
      setLineConfigured(res.data.line_configured);
    } catch { setFbConfigured(false); setLineConfigured(false); }
  };

  const postPropertyToFacebook = async (propertyId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/facebook/publish-property/${propertyId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Posted to Facebook!');
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post to Facebook');
    }
  };

  const postPropertyToLine = async (propertyId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/line/publish-property/${propertyId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Posted to LINE Voom!');
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post to LINE');
    }
  };

  const postPropertyToAll = async (propertyId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/social/publish-property/${propertyId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const results = res.data;
      if (results.facebook?.success) toast.success('Posted to Facebook!');
      else if (results.facebook?.error) toast.error(`FB: ${results.facebook.error}`);
      if (results.line?.success) toast.success('Posted to LINE Voom!');
      else if (results.line?.error) toast.error(`LINE: ${results.line.error}`);
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) { toast.error('Failed to fetch properties'); }
    finally { setLoading(false); }
  };

  const handleLogout = () => { logout(); toast.success('Logged out'); navigate('/admin/login'); };

  const openCreateDialog = () => {
    setEditingProperty(null);
    setFormData({ title: '', description: '', property_type: 'House', listing_type: 'Sale', rental_period: '', price: '', location: '', city: '', bedrooms: '', bathrooms: '', size_sqm: '', amenities: [], images: [], videos: [], status: 'active', co_agent: false, banner: null, featured: false });
    setDialogOpen(true);
  };

  const openEditDialog = (property) => {
    setEditingProperty(property);
    setFormData({
      title: property.title, description: property.description, property_type: property.property_type,
      listing_type: property.listing_type, rental_period: property.rental_period || '', price: property.price.toString(),
      location: property.location, city: property.city, bedrooms: property.bedrooms?.toString() || '',
      bathrooms: property.bathrooms?.toString() || '', size_sqm: property.size_sqm?.toString() || '',
      amenities: property.amenities || [], images: property.images || [], videos: property.videos || [], status: property.status,
      co_agent: property.co_agent || false, banner: property.banner || null, featured: property.featured || false
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, price: parseFloat(formData.price), bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null, bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null, size_sqm: formData.size_sqm ? parseFloat(formData.size_sqm) : null, rental_period: formData.listing_type === 'Rental' ? formData.rental_period : null, videos: formData.videos || [], featured: formData.featured || false };
    try {
      if (editingProperty) { await axios.put(`${API}/properties/${editingProperty.id}`, payload); toast.success('Property updated'); }
      else { await axios.post(`${API}/properties`, payload); toast.success('Property created'); }
      setDialogOpen(false); fetchProperties();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/properties/${id}`); toast.success('Deleted'); fetchProperties(); }
    catch (error) { toast.error('Failed to delete'); }
  };

  const openQuickEditImages = (property) => {
    setQuickEditProperty(property); setQuickImageUrls(''); setQuickImageAction('add'); setQuickEditImagesOpen(true);
  };

  const handleQuickImageUpdate = async () => {
    if (!quickImageUrls.trim()) { toast.error('Enter at least one image URL'); return; }
    const urls = quickImageUrls.split('\n').map(u => u.trim()).filter(u => u);
    if (!urls.length) { toast.error('Enter valid image URLs'); return; }
    try {
      const response = await axios.patch(`${API}/properties/${quickEditProperty.id}/images`, { image_urls: urls, action: quickImageAction });
      toast.success(`Images updated! Total: ${response.data.total_images}`);
      setQuickEditImagesOpen(false); fetchProperties();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update images'); }
  };

  const handleExtractImagesFromLineVoom = async () => {
    if (!lineVoomExtractUrl.trim()) { toast.error('Enter a Line Voom URL'); return; }
    if (!lineVoomExtractUrl.includes('linevoom.line.me')) { toast.error('Enter a valid Line Voom URL'); return; }
    setExtractingImages(true);
    try {
      const response = await axios.post(`${API}/extract-images-from-linevoom`, { url: lineVoomExtractUrl });
      if (response.data.image_urls?.length > 0) { setQuickImageUrls(response.data.image_urls.join('\n')); toast.success(`Found ${response.data.count} images!`); }
      else { toast.error('No images found.'); }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to extract'); }
    finally { setExtractingImages(false); }
  };

  const addAmenity = () => { if (newAmenity.trim()) { setFormData({ ...formData, amenities: [...formData.amenities, newAmenity.trim()] }); setNewAmenity(''); } };
  const removeAmenity = (index) => { setFormData({ ...formData, amenities: formData.amenities.filter((_, i) => i !== index) }); };
  const [imageUploading, setImageUploading] = useState(false);
  const [gdriveInput, setGdriveInput] = useState('');
  const [gdriveImporting, setGdriveImporting] = useState(false);
  const dropZoneRef = React.useRef(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  const uploadFileToServer = async (file) => {
    const token = localStorage.getItem('token');
    const fd = new FormData();
    fd.append('file', file);
    const isVideo = file.type.startsWith('video/');
    const endpoint = isVideo ? `${API}/upload/video` : `${API}/upload/image`;
    const res = await axios.post(endpoint, fd, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      timeout: 300000
    });
    return res.data.url;
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImageUploading(true);
    try {
      for (const file of files) {
        toast.info(`Uploading ${file.name}...`);
        const url = await uploadFileToServer(file);
        setFormData(prev => ({ ...prev, images: [...prev.images, url] }));
      }
      toast.success(`${files.length} image(s) uploaded!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setImageUploading(false);
    }
    e.target.value = '';
  };

  const handleDropZone = async (e) => {
    e.preventDefault();
    setIsDraggingFile(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (!files.length) { toast.error('Please drop image files'); return; }
    setImageUploading(true);
    try {
      for (const file of files) {
        toast.info(`Uploading ${file.name}...`);
        const url = await uploadFileToServer(file);
        setFormData(prev => ({ ...prev, images: [...prev.images, url] }));
      }
      toast.success(`${files.length} image(s) uploaded!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setImageUploading(false);
    }
  };

  const handleCameraCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      toast.info('Uploading photo...');
      const url = await uploadFileToServer(file);
      setFormData(prev => ({ ...prev, images: [...prev.images, url] }));
      toast.success('Photo uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setImageUploading(false);
    }
    e.target.value = '';
  };

  const handleGdriveImport = async () => {
    if (!gdriveInput.trim()) { toast.error('Paste Google Drive links'); return; }
    const links = gdriveInput.split(/[,\n]/).map(l => l.trim()).filter(Boolean);
    const fileIds = links.map(link => {
      const match = link.match(/\/d\/([a-zA-Z0-9_-]+)/);
      return match ? match[1] : null;
    }).filter(Boolean);
    if (!fileIds.length) { toast.error('No valid Google Drive links found'); return; }
    setGdriveImporting(true);
    toast.info(`Importing ${fileIds.length} images from Google Drive...`);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/upload/gdrive-batch`, { file_ids: fileIds }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 300000
      });
      const urls = res.data.urls || [];
      setFormData(prev => ({ ...prev, images: [...prev.images, ...urls] }));
      toast.success(`${urls.length} images imported from Google Drive!`);
      setGdriveInput('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google Drive import failed');
    } finally {
      setGdriveImporting(false);
    }
  };
  const addImageUrl = () => { if (imageInput.trim()) { setFormData({ ...formData, images: [...formData.images, imageInput.trim()] }); setImageInput(''); } };
  const removeImage = (index) => { setFormData({ ...formData, images: formData.images.filter((_, i) => i !== index) }); };
  const [videoUploading, setVideoUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleImageDragStart = (idx) => { setDragIndex(idx); };
  const handleImageDragOver = (e, idx) => { e.preventDefault(); setDragOverIndex(idx); };
  const handleImageDragEnd = () => {
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      const reordered = [...formData.images];
      const [moved] = reordered.splice(dragIndex, 1);
      reordered.splice(dragOverIndex, 0, moved);
      setFormData({ ...formData, images: reordered });
    }
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const token = localStorage.getItem('token');
    const fd = new FormData();
    fd.append('file', file);
    setVideoUploading(true);
    try {
      toast.info(`Uploading video: ${file.name} (${(file.size / (1024*1024)).toFixed(1)} MB)...`);
      const res = await axios.post(`${API}/upload/video`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        onUploadProgress: (progressEvent) => {
          const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (pct % 25 === 0) console.log(`Upload progress: ${pct}%`);
        }
      });
      setFormData(prev => ({ ...prev, videos: [...(prev.videos || []), res.data.url] }));
      toast.success(`Video uploaded! (${res.data.size_mb} MB)`);
    } catch (error) {
      console.error('Video upload error:', error);
      toast.error(error.response?.data?.detail || `Video upload failed: ${error.message}`);
    } finally {
      setVideoUploading(false);
    }
    e.target.value = '';
  };
  const addVideoUrl = () => { if (videoInput.trim()) { setFormData({ ...formData, videos: [...(formData.videos || []), videoInput.trim()] }); setVideoInput(''); } };
  const removeVideo = (index) => { setFormData({ ...formData, videos: (formData.videos || []).filter((_, i) => i !== index) }); };

  const inputCls = "bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 rounded-xl";

  return (
    <div className="min-h-screen bg-[#0A0E27]">
      {/* Header */}
      <header className="crystal-glass sticky top-0 z-50" data-testid="admin-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="font-heading text-2xl font-semibold text-[#D4AF37] tracking-tight cursor-pointer" onClick={() => navigate('/')} data-testid="admin-logo">
                NOK <span className="text-white/40 font-light mx-1">|</span> <span className="text-white font-light text-lg">Admin</span>
              </h1>
              <span className="text-[#94A3B8] text-sm">Welcome, {user?.username}</span>
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              {[
                { label: 'Analytics', icon: TrendingUp, path: '/admin/analytics', testId: 'analytics-button' },
                { label: 'Content', icon: FileText, path: '/admin/content', testId: 'content-button' },
                { label: 'Blog', icon: BookOpen, path: '/admin/blog', testId: 'blog-button' },
                { label: 'Services', icon: Shield, path: '/admin/services', testId: 'services-button' },
                { label: 'Users', icon: Users, path: '/admin/users', testId: 'users-button' },
                { label: 'Import', icon: Link2, path: '/admin/import', testId: 'import-properties-button' },
                { label: 'FB Import', icon: Download, path: '/admin/social-import', testId: 'social-import-button' },
                { label: 'View Site', icon: HomeIcon, path: '/', testId: 'view-site-button' },
              ].map(item => (
                <Button key={item.label} onClick={() => navigate(item.path)} variant="ghost"
                  className="text-[#94A3B8] hover:text-white hover:bg-white/5 text-xs rounded-lg h-9 px-3" data-testid={item.testId}>
                  <item.icon className="w-3.5 h-3.5 mr-1.5" /> {item.label}
                </Button>
              ))}
              <Button onClick={handleLogout} variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs rounded-lg h-9 px-3" data-testid="logout-button">
                <LogOut className="w-3.5 h-3.5 mr-1.5" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="font-heading text-4xl font-semibold text-white tracking-tight" data-testid="dashboard-title">Property Management</h2>
            <p className="text-[#94A3B8] mt-2">{properties.length} total properties</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreateDialog} className="bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold rounded-full px-6" data-testid="add-property-button">
                <Plus className="w-5 h-5 mr-2" /> Add Property
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#131A38] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-[#D4AF37]">{editingProperty ? 'Edit Property' : 'Add New Property'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="property-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Title *</Label>
                    <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className={inputCls} data-testid="property-title-input" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Description *</Label>
                    <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} required className={`${inputCls} resize-y`} data-testid="property-description-input" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Property Type *</Label>
                    <Select value={formData.property_type} onValueChange={(v) => setFormData({ ...formData, property_type: v })}>
                      <SelectTrigger className={inputCls} data-testid="property-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{['House', 'Condo', 'Land', 'Commercial', 'Retail'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Listing Type *</Label>
                    <Select value={formData.listing_type} onValueChange={(v) => setFormData({ ...formData, listing_type: v })}>
                      <SelectTrigger className={inputCls} data-testid="listing-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Sale">For Sale</SelectItem><SelectItem value="Rental">For Rent</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {formData.listing_type === 'Rental' && (
                    <div>
                      <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Rental Period *</Label>
                      <Select value={formData.rental_period} onValueChange={(v) => setFormData({ ...formData, rental_period: v })}>
                        <SelectTrigger className={inputCls} data-testid="rental-period-select"><SelectValue placeholder="Select period" /></SelectTrigger>
                        <SelectContent><SelectItem value="nightly">Nightly</SelectItem><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="annual">Annual</SelectItem></SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Price (THB) *</Label>
                    <Input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required className={inputCls} data-testid="property-price-input" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">City/Province *</Label>
                    <Select value={formData.city} onValueChange={(v) => setFormData({ ...formData, city: v })}>
                      <SelectTrigger className={inputCls} data-testid="property-city-select"><SelectValue placeholder="Select city" /></SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {['Bangkok', 'Pattaya', 'Phuket', 'Chiang Mai', 'Hua Hin', 'Koh Samui', 'Krabi', 'Chon Buri', 'Rayong', 'Chiang Rai', 'Nonthaburi', 'Samut Prakan', 'Prachuap Khiri Khan'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Location/Area *</Label>
                    <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} required className={inputCls} data-testid="property-location-input" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Bedrooms</Label>
                    <Input type="number" value={formData.bedrooms} onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })} className={inputCls} data-testid="property-bedrooms-input" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Bathrooms</Label>
                    <Input type="number" value={formData.bathrooms} onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })} className={inputCls} data-testid="property-bathrooms-input" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Size (sqm)</Label>
                    <Input type="number" step="0.01" value={formData.size_sqm} onChange={(e) => setFormData({ ...formData, size_sqm: e.target.value })} className={inputCls} data-testid="property-size-input" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Status</Label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                      <SelectTrigger className={inputCls} data-testid="property-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-3">
                    <input id="co_agent" type="checkbox" checked={formData.co_agent || false} onChange={(e) => setFormData({ ...formData, co_agent: e.target.checked })} className="h-4 w-4 rounded border-white/20 bg-[#0A0E27] text-[#D4AF37] focus:ring-[#D4AF37]/20" data-testid="co-agent-checkbox" />
                    <Label htmlFor="co_agent" className="text-[#94A3B8] text-sm">Co-Agent Property</Label>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Banner</Label>
                    <Select value={formData.banner || 'none'} onValueChange={(v) => setFormData({ ...formData, banner: v === 'none' ? null : v })}>
                      <SelectTrigger className={inputCls} data-testid="banner-select"><SelectValue placeholder="Select banner" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Banner</SelectItem>
                        <SelectItem value="HOT LISTING">HOT LISTING</SelectItem>
                        <SelectItem value="SPECIAL PROMOTION">SPECIAL PROMOTION</SelectItem>
                        <SelectItem value="PRICE DROP">PRICE DROP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Amenities */}
                <div>
                  <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-2 block">Amenities</Label>
                  <div className="flex gap-2 mb-2">
                    <Input value={newAmenity} onChange={(e) => setNewAmenity(e.target.value)} placeholder="Add amenity..." onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAmenity(); } }} className={inputCls} data-testid="amenity-input" />
                    <Button type="button" onClick={addAmenity} className="bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/30 rounded-xl" data-testid="add-amenity-button">Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.amenities.map((amenity, idx) => (
                      <span key={idx} className="px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-xs flex items-center gap-2">
                        {amenity}
                        <button type="button" onClick={() => removeAmenity(idx)} className="hover:text-red-400"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                </div>
                {/* Images */}
                <div>
                  <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-2 block">Images</Label>

                  {/* Drag & Drop Zone */}
                  <div
                    ref={dropZoneRef}
                    onDragOver={(e) => { e.preventDefault(); setIsDraggingFile(true); }}
                    onDragLeave={() => setIsDraggingFile(false)}
                    onDrop={handleDropZone}
                    className={`border-2 border-dashed rounded-xl p-6 text-center transition-all duration-300 mb-3 ${
                      isDraggingFile ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 bg-[#0A0E27]/50'
                    }`}
                    data-testid="image-drop-zone"
                  >
                    {imageUploading ? (
                      <div className="flex items-center justify-center gap-3">
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#D4AF37] border-r-transparent" />
                        <span className="text-[#D4AF37] text-sm">Uploading...</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-[#D4AF37]/40 mx-auto mb-2" />
                        <p className="text-[#94A3B8] text-sm mb-1">Drop images here</p>
                        <p className="text-[#475569] text-xs">or use the options below</p>
                      </>
                    )}
                  </div>

                  {/* Upload Options Row */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {/* File Upload (multi-select) */}
                    <label className="flex items-center justify-center gap-2 py-2.5 px-3 bg-[#0A0E27] border border-white/10 rounded-xl cursor-pointer hover:border-[#D4AF37]/30 transition-colors" data-testid="image-file-label">
                      <Upload className="w-4 h-4 text-[#D4AF37]" />
                      <span className="text-[#94A3B8] text-xs">Upload Files</span>
                      <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" data-testid="image-file-input" />
                    </label>
                    {/* Camera Capture */}
                    <label className="flex items-center justify-center gap-2 py-2.5 px-3 bg-[#0A0E27] border border-white/10 rounded-xl cursor-pointer hover:border-[#D4AF37]/30 transition-colors" data-testid="camera-capture-label">
                      <ImageIcon className="w-4 h-4 text-[#D4AF37]" />
                      <span className="text-[#94A3B8] text-xs">Take Photo</span>
                      <input type="file" accept="image/*" capture="environment" onChange={handleCameraCapture} className="hidden" data-testid="camera-input" />
                    </label>
                  </div>

                  {/* URL Paste */}
                  <div className="flex gap-2 mb-2">
                    <Input value={imageInput} onChange={(e) => setImageInput(e.target.value)} placeholder="Paste image URL..." onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addImageUrl(); } }} className={`flex-1 ${inputCls}`} data-testid="image-url-input" />
                    <Button type="button" onClick={addImageUrl} className="bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/30 rounded-xl text-xs" data-testid="add-image-url-button">Add URL</Button>
                  </div>

                  {/* Google Drive Import */}
                  <div className="bg-[#0A0E27]/50 border border-white/5 rounded-xl p-3 mb-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Download className="w-3.5 h-3.5 text-[#D4AF37]" />
                      <span className="text-[#94A3B8] text-xs font-medium">Import from Google Drive</span>
                    </div>
                    <Textarea value={gdriveInput} onChange={(e) => setGdriveInput(e.target.value)}
                      placeholder="Paste Google Drive links (one per line or comma-separated)..."
                      rows={2} className={`${inputCls} font-mono text-xs resize-y mb-2`} data-testid="gdrive-input" />
                    <Button type="button" onClick={handleGdriveImport} disabled={gdriveImporting}
                      className="w-full bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 rounded-lg text-xs h-8" data-testid="gdrive-import-btn">
                      {gdriveImporting ? 'Importing...' : 'Import from Google Drive'}
                    </Button>
                  </div>
                  {formData.images.length > 0 && (
                    <p className="text-[#94A3B8] text-[10px] mt-2 mb-1">Drag to reorder — first image is the thumbnail</p>
                  )}
                  <div className="grid grid-cols-4 gap-2 mt-1">
                    {formData.images.map((img, idx) => (
                      <div
                        key={idx}
                        draggable
                        onDragStart={() => handleImageDragStart(idx)}
                        onDragOver={(e) => handleImageDragOver(e, idx)}
                        onDragEnd={handleImageDragEnd}
                        className={`relative group rounded-xl overflow-hidden border-2 cursor-grab active:cursor-grabbing transition-all duration-200 ${
                          dragOverIndex === idx ? 'border-[#D4AF37] scale-105 shadow-lg shadow-[#D4AF37]/20' :
                          idx === 0 ? 'border-[#D4AF37]/50' : 'border-white/5'
                        } ${dragIndex === idx ? 'opacity-40' : 'opacity-100'}`}
                        data-testid={`image-slot-${idx}`}
                      >
                        <img src={img} alt={`Property ${idx + 1}`} className="w-full h-20 object-cover pointer-events-none" />
                        {idx === 0 && (
                          <span className="absolute bottom-0 left-0 right-0 bg-[#D4AF37] text-[#0A0E27] text-[8px] font-bold text-center py-0.5">THUMBNAIL</span>
                        )}
                        <button type="button" onClick={() => removeImage(idx)} className="absolute top-1 right-1 bg-red-500/80 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-image-${idx}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Videos */}
                <div>
                  <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-2 block">Videos</Label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input type="file" accept="video/mp4,video/mov,video/avi,video/webm,video/x-m4v,video/*" onChange={handleVideoUpload} disabled={videoUploading} className={`${inputCls} flex-1`} data-testid="video-file-input" />
                      {videoUploading && <span className="text-[#D4AF37] text-xs self-center animate-pulse">Uploading...</span>}
                    </div>
                    <div className="flex gap-2">
                      <Input value={videoInput} onChange={(e) => setVideoInput(e.target.value)} placeholder="Or paste video URL (YouTube, MP4)..." onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addVideoUrl(); } }} className={inputCls} data-testid="video-url-input" />
                      <Button type="button" onClick={addVideoUrl} className="bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/30 rounded-xl" data-testid="add-video-url-button">Add URL</Button>
                    </div>
                  </div>
                  {(formData.videos || []).length > 0 && (
                    <div className="space-y-2 mt-3">
                      {formData.videos.map((vid, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-[#0A0E27] border border-white/10 rounded-xl p-3">
                          <div className="w-8 h-8 rounded bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                            <div className="w-0 h-0 border-t-[5px] border-t-transparent border-l-[9px] border-l-[#D4AF37] border-b-[5px] border-b-transparent ml-0.5" />
                          </div>
                          <span className="text-[#94A3B8] text-xs truncate flex-1">{vid.split('/').pop() || vid}</span>
                          <button type="button" onClick={() => removeVideo(idx)} className="text-red-400 hover:text-red-300 p-1"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Featured Toggle */}
                <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <input id="featured" type="checkbox" checked={formData.featured || false} onChange={(e) => setFormData({ ...formData, featured: e.target.checked })} className="h-5 w-5 rounded border-white/20 bg-[#0A0E27] text-[#D4AF37] focus:ring-[#D4AF37]/20" data-testid="featured-checkbox" />
                    <div>
                      <Label htmlFor="featured" className="text-[#D4AF37] font-semibold text-sm cursor-pointer">Exclusive / Featured Property</Label>
                      <p className="text-[#94A3B8] text-xs mt-0.5">Show this property in the "Exclusive Properties" section on the homepage</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="submit" className="flex-1 bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold rounded-full" data-testid="save-property-button">{editingProperty ? 'Update Property' : 'Create Property'}</Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10 text-[#94A3B8] hover:bg-white/5 rounded-full" data-testid="cancel-property-button">Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Properties List */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-solid border-[#D4AF37] border-r-transparent" />
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-20 bg-[#131A38] rounded-2xl border border-white/5">
            <Building2 className="w-12 h-12 mx-auto text-[#D4AF37]/40 mb-4" />
            <p className="text-[#94A3B8] mb-4">No properties yet</p>
            <Button onClick={openCreateDialog} className="bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] rounded-full">
              <Plus className="w-5 h-5 mr-2" /> Add Your First Property
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-properties-grid">
            {properties.map((property) => (
              <div key={property.id} className="bg-[#131A38] rounded-2xl overflow-hidden border border-white/5 hover:border-[#D4AF37]/20 transition-colors duration-300" data-testid={`admin-property-card-${property.id}`}>
                <div className="relative h-48 bg-[#0A0E27]">
                  {property.images?.length > 0 ? (
                    <img src={property.images[0]} alt={property.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex items-center justify-center h-full"><Building2 className="w-12 h-12 text-[#D4AF37]/20" /></div>
                  )}
                  <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${property.status === 'active' ? 'bg-emerald-500/90 text-white' : 'bg-white/20 text-white'}`}>{property.status}</span>
                    {property.co_agent && <span className="px-3 py-1 rounded-full text-[10px] font-bold bg-purple-500/90 text-white uppercase tracking-wider">Co-Agent</span>}
                  </div>
                  {property.banner && (
                    <span className={`absolute top-3 right-3 px-3 py-1 text-[10px] font-bold rounded-full uppercase tracking-wider ${
                      property.banner === 'HOT LISTING' ? 'bg-red-500/90 text-white' : property.banner === 'PRICE DROP' ? 'bg-emerald-500/90 text-white' : 'bg-orange-500/90 text-white'
                    }`}>{property.banner}</span>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-lg font-semibold text-white mb-1 line-clamp-1">{property.title}</h3>
                  <p className="text-[#94A3B8] text-sm mb-1">{property.city}</p>
                  <p className="text-[#D4AF37] font-semibold mb-2">&#3647;{new Intl.NumberFormat('en-US').format(property.price)}</p>
                  {property.facebook_posted && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-blue-400 mb-1"><Check className="w-3 h-3" /> FB</span>
                  )}
                  {property.line_posted && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-green-400 mb-3 ml-2"><Check className="w-3 h-3" /> LINE</span>
                  )}
                  {!property.facebook_posted && !property.line_posted && <div className="mb-3" />}
                  <div className="flex gap-2 flex-wrap">
                    <Button onClick={() => openEditDialog(property)} variant="outline" size="sm" className="flex-1 border-white/10 text-[#94A3B8] hover:text-white hover:bg-white/5 rounded-xl text-xs" data-testid={`edit-property-${property.id}`}>
                      <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button onClick={() => openQuickEditImages(property)} variant="outline" size="sm" className="border-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-xl" title="Quick Edit Images">
                      <ImageIcon className="w-3.5 h-3.5" />
                    </Button>
                    <Button onClick={() => postPropertyToFacebook(property.id)} variant="outline" size="sm"
                      disabled={property.facebook_posted}
                      className={`rounded-xl ${property.facebook_posted ? 'border-blue-500/40 text-blue-300 opacity-60' : 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10'}`}
                      title={property.facebook_posted ? 'Already posted to Facebook' : 'Post to Facebook'}
                      data-testid={`fb-post-${property.id}`}
                    >
                      {property.facebook_posted ? <Check className="w-3.5 h-3.5" /> : <Facebook className="w-3.5 h-3.5" />}
                    </Button>
                    <Button onClick={() => postPropertyToLine(property.id)} variant="outline" size="sm"
                      disabled={property.line_posted}
                      className={`rounded-xl ${property.line_posted ? 'border-green-500/40 text-green-300 opacity-60' : 'border-green-500/30 text-green-400 hover:bg-green-500/10'}`}
                      title={property.line_posted ? 'Already posted to LINE' : 'Post to LINE Voom'}
                      data-testid={`line-post-${property.id}`}
                    >
                      {property.line_posted ? <Check className="w-3.5 h-3.5" /> : <MessageSquare className="w-3.5 h-3.5" />}
                    </Button>
                    <Button onClick={() => postPropertyToAll(property.id)} variant="outline" size="sm"
                      disabled={property.facebook_posted && property.line_posted}
                      className={`rounded-xl ${property.facebook_posted && property.line_posted ? 'border-[#D4AF37]/20 text-[#D4AF37]/40 opacity-60' : 'border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10'}`}
                      title="Post to All Platforms"
                      data-testid={`post-all-${property.id}`}
                    >
                      <Share2 className="w-3.5 h-3.5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="border-red-500/20 text-red-400 hover:bg-red-500/10 rounded-xl" data-testid={`delete-property-${property.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#131A38] border-white/10 text-white">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">Delete Property</AlertDialogTitle>
                          <AlertDialogDescription className="text-[#94A3B8]">Are you sure you want to delete "{property.title}"? This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="border-white/10 text-[#94A3B8] hover:bg-white/5">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(property.id)} className="bg-red-500 hover:bg-red-600 text-white">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Edit Images Dialog */}
      <Dialog open={quickEditImagesOpen} onOpenChange={setQuickEditImagesOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#131A38] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#D4AF37]">Quick Edit Images</DialogTitle>
          </DialogHeader>
          {quickEditProperty && (
            <div className="space-y-4">
              <div className="bg-[#0A0E27] p-4 rounded-xl border border-white/5">
                <p className="font-semibold text-white">{quickEditProperty.title}</p>
                <p className="text-sm text-[#94A3B8] mt-1">Current images: {quickEditProperty.images?.length || 0} — Drag to reorder, first = thumbnail</p>
                {quickEditProperty.images?.length > 0 && (
                  <div className="mt-3 grid grid-cols-5 gap-2">
                    {quickEditProperty.images.map((img, idx) => (
                      <div
                        key={idx}
                        draggable
                        onDragStart={() => setDragIndex(idx)}
                        onDragOver={(e) => { e.preventDefault(); setDragOverIndex(idx); }}
                        onDragEnd={() => {
                          if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
                            const reordered = [...quickEditProperty.images];
                            const [moved] = reordered.splice(dragIndex, 1);
                            reordered.splice(dragOverIndex, 0, moved);
                            setQuickEditProperty({ ...quickEditProperty, images: reordered });
                            // Save immediately
                            const token = localStorage.getItem('token');
                            axios.patch(`${API}/properties/${quickEditProperty.id}/images`, { image_urls: reordered, action: 'replace' }, { headers: { Authorization: `Bearer ${token}` } })
                              .then(() => { toast.success('Image order saved!'); fetchProperties(); })
                              .catch(() => toast.error('Failed to save order'));
                          }
                          setDragIndex(null);
                          setDragOverIndex(null);
                        }}
                        className={`relative group rounded-lg overflow-hidden cursor-grab active:cursor-grabbing border-2 transition-all duration-200 ${
                          dragOverIndex === idx ? 'border-[#D4AF37] scale-105 shadow-lg shadow-[#D4AF37]/20' :
                          idx === 0 ? 'border-[#D4AF37]/50' : 'border-white/10'
                        } ${dragIndex === idx ? 'opacity-40' : ''}`}
                        data-testid={`quick-img-${idx}`}
                      >
                        <img src={img} alt={`${idx + 1}`} className="h-20 w-full object-cover pointer-events-none" />
                        {idx === 0 && (
                          <span className="absolute bottom-0 left-0 right-0 bg-[#D4AF37] text-[#0A0E27] text-[7px] font-bold text-center py-0.5">THUMBNAIL</span>
                        )}
                        <button
                          type="button"
                          onClick={async () => {
                            const newImages = quickEditProperty.images.filter((_, i) => i !== idx);
                            const token = localStorage.getItem('token');
                            try {
                              await axios.patch(`${API}/properties/${quickEditProperty.id}/images`, { image_urls: newImages, action: 'replace' }, { headers: { Authorization: `Bearer ${token}` } });
                              setQuickEditProperty({ ...quickEditProperty, images: newImages });
                              toast.success('Image removed');
                              fetchProperties();
                            } catch { toast.error('Failed to remove'); }
                          }}
                          className="absolute top-1 right-1 bg-red-500/80 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Action</Label>
                <Select value={quickImageAction} onValueChange={setQuickImageAction}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="add">Add to existing</SelectItem><SelectItem value="replace">Replace all</SelectItem></SelectContent>
                </Select>
              </div>

              {/* Quick Upload Options */}
              <div className="grid grid-cols-2 gap-2">
                <label className="flex items-center justify-center gap-2 py-2.5 px-3 bg-[#0A0E27] border border-white/10 rounded-xl cursor-pointer hover:border-[#D4AF37]/30 transition-colors">
                  <Upload className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-[#94A3B8] text-xs">Upload Files</span>
                  <input type="file" accept="image/*" multiple onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    if (!files.length) return;
                    for (const file of files) {
                      try {
                        toast.info(`Uploading ${file.name}...`);
                        const url = await uploadFileToServer(file);
                        setQuickImageUrls(prev => prev ? prev + '\n' + url : url);
                      } catch { toast.error('Upload failed'); }
                    }
                    toast.success(`${files.length} image(s) ready — click Add Images to save`);
                    e.target.value = '';
                  }} className="hidden" />
                </label>
                <label className="flex items-center justify-center gap-2 py-2.5 px-3 bg-[#0A0E27] border border-white/10 rounded-xl cursor-pointer hover:border-[#D4AF37]/30 transition-colors">
                  <ImageIcon className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-[#94A3B8] text-xs">Take Photo</span>
                  <input type="file" accept="image/*" capture="environment" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      toast.info('Uploading photo...');
                      const url = await uploadFileToServer(file);
                      setQuickImageUrls(prev => prev ? prev + '\n' + url : url);
                      toast.success('Photo ready — click Add Images to save');
                    } catch { toast.error('Upload failed'); }
                    e.target.value = '';
                  }} className="hidden" />
                </label>
              </div>

              <div className="bg-[#D4AF37]/5 p-4 rounded-xl border border-[#D4AF37]/20">
                <Label className="text-[#D4AF37] font-medium mb-2 block text-sm">Auto Extract from Line Voom</Label>
                <div className="flex gap-2">
                  <Input value={lineVoomExtractUrl} onChange={(e) => setLineVoomExtractUrl(e.target.value)} placeholder="https://linevoom.line.me/post/..." className={inputCls} />
                  <Button onClick={handleExtractImagesFromLineVoom} disabled={extractingImages} className="bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold rounded-xl px-4 flex-shrink-0">
                    {extractingImages ? 'Extracting...' : 'Extract'}
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Image URLs (one per line)</Label>
                <Textarea value={quickImageUrls} onChange={(e) => setQuickImageUrls(e.target.value)} placeholder="Paste image URLs here..." rows={5} className={`${inputCls} font-mono text-sm resize-y`} />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleQuickImageUpdate} className="flex-1 bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold rounded-full">
                  <ImageIcon className="w-4 h-4 mr-2" /> {quickImageAction === 'add' ? 'Add Images' : 'Replace Images'}
                </Button>
                <Button variant="outline" onClick={() => setQuickEditImagesOpen(false)} className="border-white/10 text-[#94A3B8] hover:bg-white/5 rounded-full">Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
